import * as React from "react";
import Select from "react-select";
import { Styles } from "react-select/lib/styles";
import { MenuPlacement, ValueType } from "react-select/lib/types";
import { getID } from "../../../utils/IdGenerator";
import { noop } from "lodash";

export type SelectOption = {
	value: string;
	label: string;
};

type SelectProps<T, S extends SelectOption> = {
	/**
	 * The visible title of the dropdown.
	 */
	label?: string;

	/**
	 * Function mapping each item to its display name.
	 */
	itemToOption: (item: T) => S;

	/**
	 * All the items.
	 */
	items: T[];
	isSearchable?: boolean;
	name: string;
	labelClasses?: string;
	menuPlacement?: MenuPlacement;
	ariaLabel?: string;
	required?: boolean;
	disabled?: boolean;
	onBlur?: () => void;
	formatOptionLabel?: (option: S) => React.ReactNode;
	placeholder?: string;
	isClearable?: boolean;
};

type SelectState = {
	id: string;
};

type SingleProps<T, S extends SelectOption> = SelectProps<T, S> & {
	onChange: (item: T | undefined) => void;
	selectedItem?: T;
};

/**
 * A dropdown control for selecting one item from a generic collection.
 */
export class SelectSingleInput<T, S extends SelectOption> extends React.PureComponent<
	SingleProps<T, S>,
	SelectState
> {
	private select!: Select<any>;

	private readonly colourStyles: Partial<Styles> = {
		control: styles => ({ ...styles, backgroundColor: "white", borderColor: "#d6d6d6" }),
		dropdownIndicator: styles => ({ ...styles, color: "black" }),
		indicatorSeparator: styles => ({ ...styles, backgroundColor: "transparent" }),
		menu: styles => ({ ...styles, zIndex: 2 })
	};
	constructor(props: SingleProps<T, S>) {
		super(props);
		this.state = {
			id: getID()
		};
	}

	public focus = () => this.select.focus();

	public render() {
		return (
			<div className="form-item form-group form-inline form-item form-type-select form-group">
				<input
					required={this.props.required}
					defaultValue={
						this.props.selectedItem
							? this.props.itemToOption(this.props.selectedItem).value
							: undefined
					}
					id={this.state.id}
					onFocus={this.select ? this.select.focus : noop}
					tabIndex={2}
					style={{ opacity: 0, height: "0px", border: 0, padding: 0 }}
				/>
				{this.props.label && (
					<label className={"control-label " + this.props.labelClasses} htmlFor={this.state.id}>
						{this.props.label}
						{this.props.required === true && (
							<span className="form-required" title="This field is required" aria-hidden="true">
								*
							</span>
						)}
					</label>
				)}
				<Select
					isClearable={this.props.isClearable}
					isDisabled={this.props.disabled}
					options={this.props.items.map(this.props.itemToOption)}
					value={this.props.selectedItem ? this.props.itemToOption(this.props.selectedItem) : null}
					onChange={this.onChange}
					isSearchable
					styles={this.colourStyles}
					inputId={this.state.id}
					onBlur={this.props.onBlur}
					placeholder={this.props.placeholder ?? 'Search...'}
					menuPlacement={this.props.menuPlacement || "auto"}
					openMenuOnFocus
					ref={(ref: Select<any>) => {
						this.select = ref;
					}}
					formatOptionLabel={this.props.formatOptionLabel}
				/>
				{this.props.required && !this.props.selectedItem && (
					<div className="error-select">
						<label className="new-error" htmlFor={this.state.id}>
							This field is required
						</label>
					</div>
				)}
			</div>
		);
	}

	private readonly onChange = (selected?: ValueType<S>) => {
		if (selected === null || selected === undefined) {
			this.props.onChange(undefined);
			return;
		}

		const item = this.props.items.find(
			i => this.props.itemToOption(i).value === (selected as S).value
		);
		if (!item) {
			throw new Error("Unknown option selected");
		}
		this.props.onChange(item);
	};
}

type MultiProps<T, S extends SelectOption> = SelectProps<T, S> & {
	onChange: (items: T[]) => void;
	selectedItems: T[];
};

/**
 * A dropdown control for selecting multiple items from a collection.
 */
export class SelectMultiInput<T, S extends SelectOption> extends React.PureComponent<
	MultiProps<T, S>,
	SelectState
> {
	public static getDerivedStateFromProps<T, S extends SelectOption>(
		_nextProps: MultiProps<T, S>,
		oldState: SelectState
	): SelectState {
		return {
			...oldState
		};
	}

	private readonly select: React.RefObject<Select<S>>;

	private readonly colourStyles: Partial<Styles> = {
		control: styles => ({ ...styles, backgroundColor: "white", borderColor: "#d6d6d6" }),
		dropdownIndicator: styles => ({ ...styles, color: "black" }),
		indicatorSeparator: styles => ({ ...styles, backgroundColor: "transparent" })
	};

	constructor(props: MultiProps<T, S>) {
		super(props);
		this.select = React.createRef();
		this.state = {
			id: getID()
		};
	}

	public focus = () => this.select.current!.focus();

	public render() {
		const selectedOptions = this.props.selectedItems.map(this.props.itemToOption);

		return (
			<div className="form-item form-group form-inline form-item form-type-select form-group">
				<input
					required={this.props.required}
					defaultValue={this.props.selectedItems ? selectedOptions.map(o => o.value) : undefined}
					id={this.state.id}
					onFocus={() => this.select.current!.focus()}
					tabIndex={2}
					style={{ opacity: 0, height: "0px" }}
				/>
				{this.props.label && (
					<label className={"control-label " + this.props.labelClasses} htmlFor={this.state.id}>
						{this.props.label}
					</label>
				)}
				<Select
					options={this.props.items.map(this.props.itemToOption)}
					value={selectedOptions}
					onChange={this.onChange}
					isSearchable
					isMulti
					styles={this.colourStyles}
					inputId={this.state.id}
					placeholder="Select..."
					menuPlacement={this.props.menuPlacement || "auto"}
					openMenuOnFocus
					ref={this.select}
					formatOptionLabel={this.props.formatOptionLabel}
				/>
				{this.props.required &&
					(!this.props.selectedItems || this.props.selectedItems.length === 0) && (
						<div className="error-select">
							<label className="new-error" htmlFor={this.state.id}>
								This field is required
							</label>
						</div>
					)}
			</div>
		);
	}

	private readonly onChange = (selected?: ValueType<S>) => {
		if (!selected) {
			this.props.onChange([]);
			return;
		}

		if (Array.isArray(selected)) {
			const items = this.props.items.filter(i => {
				const value = this.props.itemToOption(i).value;
				return selected.some(s => s.value === value);
			});
			this.props.onChange(items);
			return;
		}

		const items = this.props.items.filter(i => {
			const value = this.props.itemToOption(i).value;
			return (selected as SelectOption).value === value;
		});
		this.props.onChange(items);
	};
}
